import * as React from 'react'

import { format, isToday } from 'date-fns'
import { PhoneOff, Smartphone, TriangleAlert, Unlink } from 'lucide-react'
import { MobXProviderContext, observer } from 'mobx-react'

import {
  IPhoneActivityLogQuery,
  usePhoneActivityLogQuery,
  useRemovePhoneNumberMutation,
  useTransferPhoneNumberMutation,
} from '~/client/graph/operations/generated/PhoneTransfer.generated'
import Avatar, {
  AvatarSize,
} from '~/client/src/shared/components/Avatar/Avatar'
import { PopoverTagPicker } from '~/client/src/shared/components/PopoverTagPicker/PopoverTagPicker'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '~/client/src/shared/components/shadcn-ui/dialog'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '~/client/src/shared/components/shadcn-ui/tabs'
import { Tag } from '~/client/src/shared/components/Tag/Tag'
import UserListSelector, {
  GroupingOption,
} from '~/client/src/shared/components/UserListSelector/UserListSelector'
import { useUserListSelectorData } from '~/client/src/shared/components/UserListSelector/useUserListSelectorData'
import UsernameFromUid from '~/client/src/shared/components/UsernameFromUid'
import UserProfilePreview from '~/client/src/shared/components/UserProfilePreview/UserProfilePreview'
import AvatarType from '~/client/src/shared/enums/AvatarType'
import User from '~/client/src/shared/models/User'
import UserProject from '~/client/src/shared/models/UserProject'
import { toast } from '~/client/src/shared/utils/toast'

import { IUserRow } from '../types'

export interface PhoneActionHandlers {
  onManagePhone: (userId: string) => void
}

export interface UsePhoneActionsResult {
  handlers: PhoneActionHandlers
  dialogs: React.ReactElement
}

// strandSource is auth-only, so it's surfaced inline after a requiresConfirmation
// refusal and confirmed on the next click of the action that raised it.
type StrandPrompt = { kind: 'transfer' | 'remove'; message: string }

function displayName(user: IUserRow): string {
  return user.fullName || user.email || user.id
}

// Pure view-model for the Details tab, extracted for unit testing.
export interface PhoneActionInputs {
  hasTarget: boolean
  targetHasNumber: boolean
  reactiveReplace: boolean
  strandKind: 'transfer' | 'remove' | null
}

export function derivePhoneActionView(s: PhoneActionInputs): {
  showReplaceWarning: boolean
  confirmReplaceTarget: boolean
  confirmStrandSourceTransfer: boolean
  confirmStrandSourceRemove: boolean
  transferLabel: string
  unlinkLabel: string
} {
  const showReplaceWarning =
    s.hasTarget && (s.targetHasNumber || s.reactiveReplace)
  return {
    showReplaceWarning,
    confirmReplaceTarget: s.targetHasNumber || s.reactiveReplace,
    confirmStrandSourceTransfer: s.strandKind === 'transfer',
    confirmStrandSourceRemove: s.strandKind === 'remove',
    transferLabel:
      showReplaceWarning || s.strandKind === 'transfer'
        ? 'Transfer anyway'
        : 'Transfer',
    unlinkLabel:
      s.strandKind === 'remove' ? 'Unlink anyway' : 'Unlink phone number',
  }
}

export function transferSuccessMessage(
  number: string,
  toName: string,
  fromName: string,
  replacedNumber: string | null,
): string {
  const replacedClause = replacedNumber ? ` (replacing ${replacedNumber})` : ''
  return `${number} was assigned to ${toName}${replacedClause} and detached from ${fromName}.`
}

export function removeSuccessMessage(number: string, name: string): string {
  return `${number} was unlinked from ${name}.`
}

type PhoneActivityRow = NonNullable<
  NonNullable<IPhoneActivityLogQuery['phoneActivityLogs']>[number]
>

function formatWhen(value: unknown): string {
  if (value == null) return ''
  const date = new Date(value as string | number)
  if (Number.isNaN(date.getTime())) return ''
  return isToday(date)
    ? `Today, ${format(date, 'p')}`
    : format(date, 'd MMM, p')
}

function strong(text: string): React.ReactElement {
  return <span className="tw:font-medium tw:text-foreground">{text}</span>
}

// Audit entries record ATTEMPTS: a Failed/Pending row must not read like a
// completed change. Completed + PartialFirebaseFailed both mean the number
// actually moved (the latter just needs a credential reconcile).
function statusBadge(
  status?: string | null,
): { label: string; className: string } | null {
  if (!status || status === 'Completed' || status === 'PartialFirebaseFailed') {
    return null
  }
  if (status === 'Pending') {
    return {
      label: 'pending',
      className: 'tw:bg-muted tw:text-muted-foreground',
    }
  }
  return {
    label: 'failed',
    className: 'tw:bg-destructive/10 tw:text-destructive',
  }
}

function initialsFromName(name?: string | null): string {
  if (!name) return '?'
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(word => word[0]!.toUpperCase())
    .join('')
}

// UsernameFromUid resolves against the current project's members: it renders a
// clickable name (opening the user card) for our own members and "Unknown user"
// for anyone it can't resolve — exactly the wanted behavior for cross-tenant log
// entries, so no local member check is needed.
function logName(
  userId: string | null | undefined,
  className: string,
): React.ReactElement {
  return (
    <span className={className}>
      <UsernameFromUid userId={userId ?? ''} />
    </span>
  )
}

function activitySentence(row: PhoneActivityRow): React.ReactNode {
  const number = row.phoneNumber || '—'
  const counterpartyNode = logName(
    row.counterpartyUserId,
    'tw:font-medium tw:text-foreground',
  )
  switch (row.changeType) {
    case 'AccountCreated':
      return <>Added phone number {strong(number)} while creating account.</>
    case 'IdentityUpdated':
      return row.replacedNumber ? (
        <>
          Changed phone number from {strong(row.replacedNumber)} to{' '}
          {strong(number)}.
        </>
      ) : (
        <>Set phone number to {strong(number)}.</>
      )
    case 'PhoneRemoved':
      return <>Unlinked phone number {strong(number)}.</>
    case 'PhoneTransferred':
      if (row.direction === 'Claimed') {
        return row.replacedNumber ? (
          <>
            Received phone number {strong(number)} from {counterpartyNode}{' '}
            (replacing {row.replacedNumber}).
          </>
        ) : (
          <>
            Received phone number {strong(number)} from {counterpartyNode}.
          </>
        )
      }
      return (
        <>
          Transferred phone number {strong(number)} to {counterpartyNode}.
        </>
      )
    default:
      return <>Phone number {strong(number)} updated.</>
  }
}

const PhoneActivityLogPanel = observer(function PhoneActivityLogPanel({
  phoneNumber,
}: {
  phoneNumber: string
}) {
  const stores = React.useContext(MobXProviderContext) as any
  // Gate the avatar on the same resolution UsernameFromUid uses for the name
  // (current user first, then project members) so a tenant-but-not-this-project
  // subject shows a generic "?" avatar next to its "Unknown user" label instead
  // of real initials — and the two never disagree during member-list load.
  const isProjectMember = (userId?: string | null): boolean =>
    !!userId &&
    (stores?.state?.user?.id === userId ||
      !!stores?.projectMembersStore?.getById?.(userId))

  // The history is admin-gated server-side and scoped to the active project.
  const projectId = stores?.state?.activeProject?.id
  const { data, loading, error } = usePhoneActivityLogQuery({
    variables: { projectId, phoneNumber },
    skip: !phoneNumber || !projectId,
    fetchPolicy: 'cache-and-network',
  })

  const rows = (data?.phoneActivityLogs ?? []).filter(
    (r): r is PhoneActivityRow => !!r,
  )

  if (error) {
    return (
      <p className="tw:py-6 tw:text-center tw:text-label-sm tw:text-destructive">
        Could not load the activity log.
      </p>
    )
  }

  if (!rows.length) {
    return (
      <p className="tw:py-6 tw:text-center tw:text-label-sm tw:text-muted-foreground">
        {loading ? 'Loading…' : 'No phone number history yet.'}
      </p>
    )
  }

  return (
    <div className="tw:max-h-[360px] tw:overflow-auto">
      {rows.map((row, index) => (
        <div
          key={`${row.occurredAt}-${index}`}
          className="tw:flex tw:gap-3 tw:border-b tw:border-border tw:py-3 last:tw:border-b-0"
        >
          <Avatar
            initials={
              isProjectMember(row.subjectUserId)
                ? initialsFromName(row.subjectName)
                : undefined
            }
            avatarType={AvatarType.User}
            size={AvatarSize.Small}
            className="tw:shrink-0"
          />
          <div className="tw:min-w-0 tw:flex-1">
            <div className="tw:flex tw:items-baseline tw:justify-between tw:gap-2">
              <span className="tw:flex tw:items-baseline tw:gap-2">
                {logName(
                  row.subjectUserId,
                  'tw:font-semibold tw:text-foreground',
                )}
                {(() => {
                  const badge = statusBadge(row.status)
                  return badge ? (
                    <span
                      className={`tw:rounded tw:px-1.5 tw:py-0.5 tw:text-label-sm ${badge.className}`}
                    >
                      {badge.label}
                    </span>
                  ) : null
                })()}
              </span>
              <span className="tw:shrink-0 tw:text-label-sm tw:text-muted-foreground">
                {formatWhen(row.occurredAt)}
              </span>
            </div>
            <p className="tw:mt-1 tw:text-body-sm tw:text-muted-foreground">
              {activitySentence(row)}
            </p>
          </div>
        </div>
      ))}
    </div>
  )
})

interface IRecipientPickerBodyProps {
  selectedId: string
  onSelect: (userId: string) => void
  onClose: () => void
}

const RecipientPickerBody = observer(function RecipientPickerBody({
  selectedId,
  onSelect,
  onClose,
}: IRecipientPickerBodyProps) {
  const stores = React.useContext(MobXProviderContext) as any
  const { users, userMetadata, currentUser, filterData } =
    useUserListSelectorData({
      projectMembersStore: stores.projectMembersStore,
      userProjectsStore: stores.userProjectsStore,
      companiesStore: stores.companiesStore,
      state: stores.state,
      projectRolesStore: stores.projectRolesStore,
      projectTeamsStore: stores.projectTeamsStore,
      projectTradesStore: stores.projectTradesStore,
      tagsStore: stores.tagsStore,
    })

  return (
    <UserListSelector
      title="Select a user"
      selectedUserIds={selectedId ? new Set([selectedId]) : new Set()}
      onUserToggle={onSelect}
      users={users}
      userMetadata={userMetadata}
      currentUser={currentUser}
      filterData={filterData}
      selectionMode="single"
      onClose={onClose}
      className="tw:flex-1 tw:min-h-0"
      defaultGrouping={GroupingOption.COMPANY}
      autoFocusSearch={true}
    />
  )
})

function InlineWarning({
  tone,
  title,
  children,
}: {
  tone: 'caution' | 'destructive'
  title: string
  children: React.ReactNode
}): React.ReactElement {
  const toneClass =
    tone === 'caution'
      ? 'tw:border-chart-2/40 tw:bg-chart-2/10 tw:text-chart-2'
      : 'tw:border-destructive/40 tw:bg-destructive/10 tw:text-destructive'
  return (
    <div className={`tw:rounded-md tw:border tw:p-3 ${toneClass}`}>
      <div className="tw:flex tw:items-start tw:gap-2">
        <TriangleAlert className="tw:mt-0.5 tw:size-4 tw:shrink-0" />
        <div>
          <p className="tw:text-label-sm tw:font-medium">{title}</p>
          <p className="tw:mt-1 tw:text-label-sm tw:text-muted-foreground">
            {children}
          </p>
        </div>
      </div>
    </div>
  )
}

export function usePhoneActions(
  users: IUserRow[],
  onCompleted?: () => void,
): UsePhoneActionsResult {
  const stores = React.useContext(MobXProviderContext) as any
  const [transferMutation] = useTransferPhoneNumberMutation()
  const [removeMutation] = useRemovePhoneNumberMutation()

  // Phone transfer/removal is gated to admins (also enforced by Graph). projectId
  // scopes that admin check to the active project.
  const projectId = stores?.state?.activeProject?.id
  const isAdmin = !!(
    stores?.state?.user &&
    stores?.userProjectsStore &&
    UserProject.isAdmin(stores.state.user, stores.userProjectsStore)
  )

  const [source, setSource] = React.useState<IUserRow | null>(null)
  const [targetId, setTargetId] = React.useState<string>('')
  const [pickerOpen, setPickerOpen] = React.useState(false)
  const [busy, setBusy] = React.useState(false)
  const [tab, setTab] = React.useState<'details' | 'log'>('details')
  const [confirmRemoveOpen, setConfirmRemoveOpen] = React.useState(false)
  const [strand, setStrand] = React.useState<StrandPrompt | null>(null)
  // Safety net for when auth reports the target owns a number but the directory
  // row didn't show one (stale).
  const [reactiveReplace, setReactiveReplace] = React.useState(false)

  // Portal the modal into #app (not <body>): #app has `isolation: isolate`, so a
  // body-level portal would paint above the whole app — including the z-999
  // sidebar that hosts the global user card. Sharing #app's stacking context lets
  // that card open in front of the modal when an assignee/log name is clicked.
  const portalContainer = React.useRef<HTMLElement | null>(null)
  if (!portalContainer.current) {
    portalContainer.current = document.getElementById('app')
  }

  const usersById = React.useMemo(() => {
    const map = new Map<string, IUserRow>()
    users.forEach(u => map.set(u.id, u))
    return map
  }, [users])

  const target = targetId ? (usersById.get(targetId) ?? null) : null
  const targetHasNumber = !!target?.phone
  const view = derivePhoneActionView({
    hasTarget: !!target,
    targetHasNumber,
    reactiveReplace,
    strandKind: strand?.kind ?? null,
  })

  const reset = React.useCallback(() => {
    setSource(null)
    setTargetId('')
    setPickerOpen(false)
    setBusy(false)
    setTab('details')
    setConfirmRemoveOpen(false)
    setStrand(null)
    setReactiveReplace(false)
  }, [])

  // In-place row update (no GET_PROJECT_MEMBERS reload/spinner). toDto drops
  // isSignUpCreated and stxAccountId, so carry them through.
  const setUserPhone = React.useCallback(
    (row: IUserRow, phone: string | null) => {
      stores?.projectMembersStore?.setOne?.({
        ...User.toDto(row.user),
        phoneNumber: phone,
        isSignUpCreated: row.user.isSignUpCreated,
        stxAccountId: row.user.stxAccountId,
      })
    },
    [stores],
  )

  const finishSuccess = React.useCallback(
    (message: string) => {
      toast.success(message)
      reset()
    },
    [reset],
  )

  const selectTarget = React.useCallback((userId: string) => {
    setTargetId(userId)
    setStrand(null)
    setReactiveReplace(false)
    setPickerOpen(false)
  }, [])

  const clearTarget = React.useCallback(() => {
    setTargetId('')
    setStrand(null)
    setReactiveReplace(false)
  }, [])

  const runRemove = React.useCallback(
    async (user: IUserRow, confirmStrandSource: boolean) => {
      setBusy(true)
      try {
        const res = await removeMutation({
          variables: { projectId, userId: user.id, confirmStrandSource },
        })
        const result = res.data?.removePhoneNumber
        // On success, don't clear busy before reset(): reset() nulls source
        // first, so the modal never renders a stale warning in a busy=false frame.
        if (result?.success) {
          const number = result.phoneNumber || user.phone
          finishSuccess(removeSuccessMessage(number, displayName(user)))
          // A success can still carry a reconcile warning (e.g. the sign-in
          // credential needs a retry) — surface it instead of a silent success.
          if (result.message) {
            toast.info(result.message)
          }
          setUserPhone(user, null)
          onCompleted?.()
          return
        }
        setBusy(false)
        if (!result) {
          toast.error('Could not remove the phone number.')
          return
        }
        if (result.requiresConfirmation) {
          setStrand({
            kind: 'remove',
            message:
              result.message || 'This user may lose the ability to sign in.',
          })
          return
        }
        toast.error(result.message || 'Could not remove the phone number.')
      } catch (err) {
        setBusy(false)
        console.error('Phone number removal failed', err)
        toast.error(
          'Could not remove the phone number. Please try again, or contact support if it keeps happening.',
        )
      }
    },
    [removeMutation, finishSuccess, setUserPhone, onCompleted, projectId],
  )

  const runTransfer = React.useCallback(
    async (
      user: IUserRow,
      to: IUserRow,
      confirmStrandSource: boolean,
      confirmReplaceTarget: boolean,
    ) => {
      const replacedNumber = confirmReplaceTarget ? to.phone : null
      setBusy(true)
      try {
        const res = await transferMutation({
          variables: {
            projectId,
            toUserId: to.id,
            phoneNumber: user.phone,
            confirmStrandSource,
            confirmReplaceTarget,
          },
        })
        const result = res.data?.transferPhoneNumber
        // On success, don't clear busy before reset(): reset() nulls source
        // first, so the modal never renders the replace warning in a busy=false
        // frame as it closes.
        if (result?.success) {
          const number = result.phoneNumber || user.phone
          finishSuccess(
            transferSuccessMessage(
              number,
              displayName(to),
              displayName(user),
              replacedNumber,
            ),
          )
          // A success can still carry a reconcile warning (e.g. the sign-in
          // credential needs a retry) — surface it instead of a silent success.
          if (result.message) {
            toast.info(result.message)
          }
          setUserPhone(user, null)
          setUserPhone(to, number)
          onCompleted?.()
          return
        }
        setBusy(false)
        if (!result) {
          toast.error('Could not transfer the phone number.')
          return
        }
        if (result.requiresConfirmation) {
          if (result.confirmationReason === 'replaceTarget') {
            setReactiveReplace(true)
          } else {
            setStrand({
              kind: 'transfer',
              message:
                result.message || 'This user may lose the ability to sign in.',
            })
          }
          return
        }
        toast.error(result.message || 'Could not transfer the phone number.')
      } catch (err) {
        setBusy(false)
        console.error('Phone number transfer failed', err)
        toast.error(
          'Could not transfer the phone number. Please try again, or contact support if it keeps happening.',
        )
      }
    },
    [transferMutation, finishSuccess, setUserPhone, onCompleted, projectId],
  )

  const handlers = React.useMemo<PhoneActionHandlers>(
    () => ({
      onManagePhone: (userId: string) => {
        const user = usersById.get(userId)
        if (!user) return
        setTargetId('')
        setPickerOpen(false)
        setStrand(null)
        setReactiveReplace(false)
        setTab('details')
        setSource(user)
      },
    }),
    [usersById],
  )

  const onTransfer = React.useCallback(() => {
    if (!source || !target) return
    void runTransfer(
      source,
      target,
      view.confirmStrandSourceTransfer,
      view.confirmReplaceTarget,
    )
  }, [source, target, view, runTransfer])

  const onUnlink = React.useCallback(() => {
    if (!source) return
    void runRemove(source, view.confirmStrandSourceRemove)
  }, [source, view, runRemove])

  const dialogs = (
    <>
      <Dialog
        open={!!source}
        // trap-focus (not full modal): clicking an assignee/log name opens the
        // global user card in the sidebar, which lives outside this popup. Full
        // modal mode would disable pointer interaction on it, so it could show
        // but not be clicked or closed. Our explicit overlay still blocks the
        // page behind; the sidebar (z-999) sits above it.
        modal="trap-focus"
        onOpenChange={open => {
          // Don't close mid-request: a late response could apply a confirmation
          // to stale source/target state.
          if (!open && !busy) reset()
        }}
      >
        <DialogContent
          className="tw:w-[460px]"
          portalContainer={portalContainer}
        >
          <DialogHeader>
            <DialogTitle>Manage Phone Number</DialogTitle>
          </DialogHeader>
          <Tabs
            value={tab}
            onValueChange={value => setTab(value as 'details' | 'log')}
          >
            <TabsList variant="line" className="tw:w-full">
              <TabsTrigger value="details">Details</TabsTrigger>
              {/* The cross-tenant history is admin-gated server-side; don't offer
                  a tab whose query is guaranteed to 403 for non-admins. */}
              {isAdmin && <TabsTrigger value="log">Log</TabsTrigger>}
            </TabsList>

            <TabsContent value="details">
              <DialogBody className="tw:flex tw:flex-col tw:gap-4">
                <div>
                  <p className="tw:text-label-sm tw:font-medium tw:text-foreground">
                    Phone number
                  </p>
                  <div className="tw:mt-1 tw:flex tw:items-center tw:text-body-sm tw:text-foreground">
                    <span className="tw:flex tw:w-10 tw:shrink-0 tw:items-center tw:justify-center">
                      <Smartphone className="tw:size-5 tw:text-muted-foreground" />
                    </span>
                    {source?.phone || '—'}
                  </div>
                </div>

                <div>
                  <p className="tw:mb-1 tw:text-label-sm tw:font-medium tw:text-foreground">
                    Assigned to
                  </p>
                  {source && (
                    <div className="tw:flex tw:items-center tw:text-body-sm tw:text-foreground">
                      <span className="tw:flex tw:w-10 tw:shrink-0 tw:items-center tw:justify-center">
                        <Avatar
                          src={source.user.avatarUrl}
                          initials={User.getInitialsToDisplay(source.user)}
                          size={AvatarSize.Small}
                        />
                      </span>
                      <UsernameFromUid userId={source.user.id} />
                    </div>
                  )}
                </div>

                {/* Transfer/unlink are admin-only (also enforced by Graph). */}
                {isAdmin && (
                  <>
                    <div className="tw:border-t tw:border-border" />

                    <div>
                      <p className="tw:mb-1 tw:text-label-sm tw:font-medium tw:text-foreground">
                        Transfer Phone Number to
                      </p>
                      <PopoverTagPicker
                        isOpen={pickerOpen}
                        onOpen={() => !busy && setPickerOpen(true)}
                        onClose={() => setPickerOpen(false)}
                        placeholder="Select a user"
                        triggerLabel="Select a user"
                        popoverClassName="tw:w-[380px] tw:max-h-[520px] tw:min-h-[420px]"
                        popoverContent={
                          pickerOpen ? (
                            <RecipientPickerBody
                              selectedId={targetId}
                              onSelect={userId => !busy && selectTarget(userId)}
                              onClose={() => setPickerOpen(false)}
                            />
                          ) : null
                        }
                      >
                        {target && (
                          <Tag
                            avatar={
                              <UserProfilePreview
                                user={target.user}
                                isCompact
                                avatarSize={AvatarSize.XMinuscule}
                              />
                            }
                            onDismiss={() => !busy && clearTarget()}
                          >
                            {displayName(target)}
                          </Tag>
                        )}
                      </PopoverTagPicker>
                    </div>

                    {source && !busy && view.showReplaceWarning && target && (
                      <InlineWarning
                        tone="caution"
                        title={`${displayName(
                          target,
                        )} already has a phone number`}
                      >
                        {target.phone ? (
                          <>
                            Transferring {source?.phone} to{' '}
                            {displayName(target)} will replace their current
                            number {target.phone}. {target.phone} will no longer
                            be assigned to anyone.
                          </>
                        ) : (
                          <>
                            Transferring {source?.phone} to{' '}
                            {displayName(target)} will replace their current
                            number, which will no longer be assigned to anyone.
                          </>
                        )}
                      </InlineWarning>
                    )}

                    {source && !busy && strand?.kind === 'transfer' && (
                      <InlineWarning
                        tone="destructive"
                        title={`${
                          source ? displayName(source) : 'This user'
                        } will lose the ability to sign in`}
                      >
                        {strand.message}
                      </InlineWarning>
                    )}

                    {source && !source.phone && (
                      <p className="tw:text-label-sm tw:text-muted-foreground">
                        This user has no phone number to transfer or unlink.
                      </p>
                    )}

                    <Button
                      className="tw:w-full"
                      disabled={busy || !targetId || !source?.phone}
                      onClick={onTransfer}
                    >
                      {view.transferLabel}
                    </Button>

                    <div className="tw:flex tw:items-center tw:gap-3 tw:text-label-sm tw:text-muted-foreground">
                      <span className="tw:h-px tw:flex-1 tw:bg-border" />
                      Or
                      <span className="tw:h-px tw:flex-1 tw:bg-border" />
                    </div>

                    <Button
                      variant="outline"
                      className="tw:w-full"
                      disabled={busy || !source?.phone}
                      onClick={() => setConfirmRemoveOpen(true)}
                    >
                      <Unlink className="tw:size-4" />
                      Unlink phone number
                    </Button>
                  </>
                )}
              </DialogBody>
            </TabsContent>

            {isAdmin && (
              <TabsContent value="log">
                <DialogBody>
                  <PhoneActivityLogPanel phoneNumber={source?.phone ?? ''} />
                </DialogBody>
              </TabsContent>
            )}
          </Tabs>
        </DialogContent>
      </Dialog>

      <Dialog
        open={confirmRemoveOpen && !!source}
        onOpenChange={open => {
          // Late responses could apply the strand confirmation to stale state.
          if (open || busy) return
          setConfirmRemoveOpen(false)
          if (strand?.kind === 'remove') setStrand(null)
        }}
      >
        <DialogContent
          className="tw:w-[420px]"
          portalContainer={portalContainer}
        >
          <DialogHeader>
            <DialogTitle className="tw:flex tw:items-center tw:gap-2">
              <PhoneOff className="tw:size-5 tw:text-destructive" />
              Unlink phone number?
            </DialogTitle>
          </DialogHeader>
          <DialogBody className="tw:flex tw:flex-col tw:gap-4">
            <p className="tw:text-body-sm tw:text-muted-foreground">
              This removes {strong(source?.phone || '—')} from{' '}
              {strong(source ? displayName(source) : 'this user')}. You can
              reassign it later from this screen.
            </p>

            {strand?.kind === 'remove' && (
              <InlineWarning
                tone="destructive"
                title={`${
                  source ? displayName(source) : 'This user'
                } will lose the ability to sign in`}
              >
                {strand.message}
              </InlineWarning>
            )}

            <div className="tw:flex tw:justify-end tw:gap-2">
              <Button
                variant="outline"
                disabled={busy}
                onClick={() => {
                  setConfirmRemoveOpen(false)
                  if (strand?.kind === 'remove') setStrand(null)
                }}
              >
                Cancel
              </Button>
              <Button variant="destructive" disabled={busy} onClick={onUnlink}>
                <Unlink className="tw:size-4" />
                {view.unlinkLabel}
              </Button>
            </div>
          </DialogBody>
        </DialogContent>
      </Dialog>
    </>
  )

  return { handlers, dialogs }
}
